// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import HistoryContext, { useHistory } from "@insite/mobius/utilities/HistoryContext";
import MiniCart from "@turnerBlueprint/App/Components/MiniCart/MiniCart";
import { color, xScreen } from "@turnerBlueprint/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    reorderLink?: boolean;
    // XNG-Change
    isPdp?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext &
    HasToasterContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};
export const productAddToCartIconButtonStyles: ButtonPresentationProps = {};

const ProductAddToCartButton = ({
    productContext: {
        product,
        productInfo: { qtyOrdered, unitOfMeasure, inventory, pricing, productDetailPath, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    reorderLink,
    toaster,
    isPdp,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const [iconButtonStyles] = useState(() => mergeToNew(productAddToCartIconButtonStyles, extendedStyles));
    const history = useHistory();
    // CDT custom
    const cartPrice =
        !hasEnoughInventory || !canAddToCart || pricing === undefined || pricing.actualPrice < 0.01
            ? undefined
            : getUnitNetPrice(pricing as ProductPriceDto, qtyOrdered || 1).priceDisplay;

    // XNG-Changes
    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };
    // eslint-disable-next-line require-await
    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        const cartline = (await addToCart({
            productId: product.id.toString(),
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog && cartPrice !== undefined) {
            toaster.addToast({
                // body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                body: (
                    <HistoryContext.Provider value={{ history }}>
                        <MiniCart
                            isPopupShown={true}
                            filterLines={[product.id]}
                            isAddToCartMessage={true}
                            qtyAdjusted={cartline.isQtyAdjusted}
                        />
                    </HistoryContext.Provider>
                ),
                messageType: "success",
                timeoutLength: 6000,
                iconProps: {
                    css: css`
                        display: none;
                    `,
                },
                cssOverrides: {
                    toast: css`
                        border: 1px solid ${color("common.ui4")};
                        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
                        background-color: ${color("common.ui1")};
                        max-height: none !important;
                        margin-top: 100px;
                        margin-right: 20px;
                        max-width: calc(100% - 32px);

                        > ${ButtonWrapper} {
                            top: 6px;
                            right: 6px;

                            ${IconWrapper} {
                                color: ${color("common.ui5")};
                            }

                            &:hover {
                                ${IconWrapper} {
                                    color: ${color("common.ui9")};
                                }
                            }
                        }

                        ${xScreen("gt", "md")} {
                            margin-top: 20px;
                            margin-right: 16px;
                            max-width: 429px;

                            &:first-of-type {
                                margin-top: 145px;
                            }
                        }
                    `,
                    toastBody: css`
                        padding: 0;
                        max-height: none;
                        width: 100%;

                        [data-test-selector="toastBody"] {
                            width: 100%;
                        }
                    `,
                },
            });
        }
    };

    // CDT custom
    const buttonLabel =
        labelOverride !== undefined
            ? labelOverride
            : cartPrice === undefined && !isPdp
            ? translate("View Product")
            : translate("Add to Cart");

    // XNG-Changes
    if (product.cantBuy) {
        return (
            <Typography
                css={
                    isPdp
                        ? css`
                              &,
                              a ${TypographyStyle} {
                                  font-size: 14px;
                                  line-height: 22px;
                              }
                          `
                        : css`
                              &,
                              a ${TypographyStyle} {
                                  font-size: 12px;
                                  line-height: 14px;
                              }
                          `
                }
            >
                {siteMessage("Custom_ProductCantBuyButton_Message")}
            </Typography>
        );
    }

    const navigateToPdp = () => {
        window.location.href = productDetailPath;
    };

    // XNG-Change
    if (reorderLink) {
        return (
            <Button
                {...iconButtonStyles}
                onClick={addToCartClickHandler}
                disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                {...otherProps}
                aria-label="add to cart icon button"
            >
                {buttonLabel}
            </Button>
        );
    }

    // CDT custom
    return cartPrice === undefined && !isPdp ? (
        <Button
            {...styles}
            onClick={navigateToPdp}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
            {...otherProps}
        >
            {buttonLabel}
        </Button>
    ) : (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            // XNG-Change
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled || product.isVariantParent}
            {...otherProps}
        >
            {buttonLabel}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(withToaster(ProductAddToCartButton)));
